import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, jobMatchAPI, coverLetterAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Target, ChevronLeft, Sparkles, AlertCircle, CheckCircle, XCircle,
  Loader2, TrendingUp, TrendingDown, FileText, ArrowRight, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const JobMatchPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [cvs, setCVs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  const [formData, setFormData] = useState({
    cv_id: '',
    job_title: '',
    company_name: '',
    job_description: ''
  });

  useEffect(() => {
    loadCVs();
  }, []);

  const loadCVs = async () => {
    try {
      const response = await cvAPI.list();
      setCVs(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, cv_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to load CVs');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!formData.cv_id) {
      toast.error('Please select a CV');
      return;
    }
    if (!formData.job_title.trim()) {
      toast.error('Please enter the job title');
      return;
    }
    if (!formData.job_description.trim()) {
      toast.error('Please paste the job description');
      return;
    }

    setAnalyzing(true);
    setAnalysis(null);
    try {
      const response = await jobMatchAPI.analyze(formData);
      setAnalysis(response.data.analysis);
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze job match');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleQuickScore = async () => {
    if (!formData.cv_id || !formData.job_description.trim()) {
      toast.error('Please select a CV and enter job description');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await jobMatchAPI.quickScore(formData);
      setAnalysis({
        match_score: response.data.match_score,
        matching_keywords: response.data.matching_keywords,
        missing_keywords: response.data.missing_keywords,
        quick_mode: true
      });
      toast.success(`Match Score: ${response.data.match_score}%`);
    } catch (error) {
      toast.error('Failed to calculate match');
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-emerald-50 border-emerald-200';
    if (score >= 50) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription Required</h1>
          <p className="text-slate-600 mb-6">Subscribe to access Job Match Analysis</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-200"></div>
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            <h1 className="font-semibold text-slate-900">Job Match Analyzer</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Intro */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 md:p-8 text-white mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">See How You Match</h2>
                <p className="text-blue-100">
                  Analyze how well your CV matches any job description. Get detailed insights on 
                  matching keywords, experience gaps, and actionable recommendations to improve your chances.
                </p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-6">Job Details</h3>
              
              <div className="space-y-5">
                {/* CV Selection */}
                <div>
                  <Label>Select Your CV *</Label>
                  <Select 
                    value={formData.cv_id} 
                    onValueChange={(value) => setFormData({ ...formData, cv_id: value })}
                  >
                    <SelectTrigger className="mt-1.5 h-11" data-testid="job-match-cv-select">
                      <SelectValue placeholder="Choose a CV" />
                    </SelectTrigger>
                    <SelectContent>
                      {cvs.map((cv) => (
                        <SelectItem key={cv.id} value={cv.id}>
                          {cv.personal_details?.full_name || cv.title || 'Untitled CV'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Job Title */}
                <div>
                  <Label>Job Title *</Label>
                  <Input
                    data-testid="job-match-title"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="e.g., Sales Representative"
                    className="mt-1.5 h-11"
                  />
                </div>

                {/* Company Name */}
                <div>
                  <Label>Company Name (optional)</Label>
                  <Input
                    data-testid="job-match-company"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g., Shoprite Holdings"
                    className="mt-1.5 h-11"
                  />
                </div>

                {/* Job Description */}
                <div>
                  <Label>Job Description *</Label>
                  <Textarea
                    data-testid="job-match-description"
                    value={formData.job_description}
                    onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                    placeholder="Paste the full job description here..."
                    className="mt-1.5 min-h-[200px]"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    data-testid="quick-match-btn"
                    onClick={handleQuickScore}
                    disabled={analyzing}
                    variant="outline"
                    className="flex-1 h-12"
                  >
                    {analyzing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>Quick Score</>
                    )}
                  </Button>
                  <Button
                    data-testid="full-analysis-btn"
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Full Analysis
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {analysis ? (
                <>
                  {/* Score Card */}
                  <div className={`rounded-2xl border-2 p-6 ${getScoreBg(analysis.match_score)}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900">Match Score</h3>
                      {analysis.match_score >= 70 ? (
                        <TrendingUp className="w-6 h-6 text-emerald-600" />
                      ) : analysis.match_score >= 50 ? (
                        <TrendingUp className="w-6 h-6 text-amber-600" />
                      ) : (
                        <TrendingDown className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                    <div className="flex items-end gap-2">
                      <span className={`text-6xl font-bold ${getScoreColor(analysis.match_score)}`}>
                        {analysis.match_score}
                      </span>
                      <span className="text-2xl text-slate-400 mb-2">%</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      {analysis.match_score >= 70 
                        ? "Great match! Your CV aligns well with this job."
                        : analysis.match_score >= 50
                        ? "Decent match. Consider tailoring your CV for better results."
                        : "Low match. Focus on highlighting transferable skills."}
                    </p>
                  </div>

                  {/* Keywords */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Keyword Analysis</h3>
                    
                    {/* Matching Keywords */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-slate-700">Matching Keywords ({analysis.matching_keywords?.length || 0})</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {analysis.matching_keywords?.length > 0 ? (
                          analysis.matching_keywords.map((keyword, idx) => (
                            <span key={idx} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm">
                              {keyword}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-slate-400">No direct keyword matches found</span>
                        )}
                      </div>
                    </div>

                    {/* Missing Keywords */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-slate-700">Missing Keywords ({analysis.missing_keywords?.length || 0})</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {analysis.missing_keywords?.length > 0 ? (
                          analysis.missing_keywords.map((keyword, idx) => (
                            <span key={idx} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                              {keyword}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-slate-400">Great! No critical keywords missing</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Experience Alignment (Full Analysis Only) */}
                  {analysis.experience_alignment && !analysis.quick_mode && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                      <h3 className="font-semibold text-slate-900 mb-4">Experience Alignment</h3>
                      
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              analysis.experience_alignment.score >= 70 ? 'bg-emerald-500' :
                              analysis.experience_alignment.score >= 50 ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${analysis.experience_alignment.score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{analysis.experience_alignment.score}%</span>
                      </div>

                      <p className="text-sm text-slate-600 mb-4">{analysis.experience_alignment.summary}</p>

                      {analysis.experience_alignment.strengths?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-emerald-700 mb-2">Strengths:</p>
                          <ul className="space-y-1">
                            {analysis.experience_alignment.strengths.map((s, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {analysis.experience_alignment.gaps?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-amber-700 mb-2">Areas to Address:</p>
                          <ul className="space-y-1">
                            {analysis.experience_alignment.gaps.map((g, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                {g}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recommendations (Full Analysis Only) */}
                  {analysis.recommendations && !analysis.quick_mode && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Lightbulb className="w-5 h-5 text-amber-500" />
                        <h3 className="font-semibold text-slate-900">Recommendations</h3>
                      </div>
                      <div className="space-y-3">
                        {analysis.recommendations.map((rec, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg border-l-4 ${
                              rec.priority === 'high' ? 'bg-red-50 border-red-500' :
                              rec.priority === 'medium' ? 'bg-amber-50 border-amber-500' :
                              'bg-slate-50 border-slate-400'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium uppercase ${
                                rec.priority === 'high' ? 'text-red-600' :
                                rec.priority === 'medium' ? 'text-amber-600' :
                                'text-slate-500'
                              }`}>
                                {rec.priority} priority
                              </span>
                            </div>
                            <p className="text-sm text-slate-700">{rec.action}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Overall Assessment (Full Analysis Only) */}
                  {analysis.overall_assessment && !analysis.quick_mode && (
                    <div className="bg-slate-900 rounded-2xl p-6 text-white">
                      <h3 className="font-semibold mb-3">Overall Assessment</h3>
                      <p className="text-slate-300 leading-relaxed">{analysis.overall_assessment}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => navigate('/cover-letter')}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Cover Letter
                    </Button>
                    <Button 
                      className="flex-1 bg-slate-900 hover:bg-slate-800"
                      onClick={() => navigate('/job-pipeline')}
                    >
                      Add to Pipeline
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                  <Target className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Ready to Analyze</h3>
                  <p className="text-slate-500 max-w-sm mx-auto">
                    Paste a job description and click "Full Analysis" to see how well your CV matches and get actionable recommendations.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* How it Works */}
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">How Job Match Works</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-3 font-bold">1</div>
                <p className="font-medium text-slate-900 mb-1">Keyword Extraction</p>
                <p className="text-sm text-slate-600">We identify key skills and requirements from the job description.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-3 font-bold">2</div>
                <p className="font-medium text-slate-900 mb-1">CV Comparison</p>
                <p className="text-sm text-slate-600">Your CV is analyzed to find matching and missing keywords.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-3 font-bold">3</div>
                <p className="font-medium text-slate-900 mb-1">Actionable Insights</p>
                <p className="text-sm text-slate-600">Get specific recommendations to improve your application.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobMatchPage;
